import React, { useEffect, useRef } from 'react';

// Define the types for the props
type AnimateFavIconProps = {
  sequences: string[]; // Array of Base64 encoded favicon strings
  loop?: number | 'infinite'; // Number of times to loop the entire sequence animation or 'infinite' for endless animation
  gap?: number; // Time gap between each frame in milliseconds
  start?: boolean; // External control to start the animation
  endCallback?: () => void; // Callback function to call when the animation ends
};

// AnimateFavIcon component to handle dynamic favicon animation
const AnimateFavIcon: React.FC<AnimateFavIconProps> = ({
  sequences,
  loop = 'infinite',
  gap = 100,
  start = true, // Default to true so animation starts automatically
  endCallback, // Callback when animation ends
}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // Store the reference for the interval
  const currentIndex = useRef<number>(0); // Track the current index of the favicon sequence
  const currentLoop = useRef<number>(0); // Track the number of times the sequence has been looped

  // Function to change the favicon dynamically
  const changeFavicon = (src: string) => {
    const link = document.createElement('link');
    const oldLink = document.getElementById('dynamic-favicon') as HTMLLinkElement;
    link.id = 'dynamic-favicon';
    link.rel = 'shortcut icon';
    link.type = 'image/png'
    link.href = src;

    // Remove the old favicon link if it exists
    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  };

  // Function to animate and change the favicon in sequence
  const animateFavicon = () => {
    if (currentIndex.current >= sequences.length) {
      if (loop === 'infinite') {
        currentIndex.current = 0; // Reset to the first index if loop is infinite
      } else if (typeof loop === 'number' && currentLoop.current >= loop) {
        // If the loop count is reached, stop the animation and call endCallback
        if (endCallback) {
          endCallback();
        }
        clearInterval(intervalRef.current!); // Stop the animation
        return;
      } else {
        // Increment loop counter when one cycle through the sequence is completed
        currentLoop.current += 1;
        currentIndex.current = 0; // Reset to the beginning of the sequence
      }
    }

    // Change the favicon to the current sequence element
    changeFavicon(sequences[currentIndex.current]);
    currentIndex.current += 1; // Move to the next frame
  };

  useEffect(() => {
    // If `start` is true, start the animation, else clear the interval
    if (start) {
      // Reset states if starting fresh
      currentIndex.current = 0;
      currentLoop.current = 0;

      // Set interval to animate the favicon
      intervalRef.current = setInterval(animateFavicon, gap);

      // Cleanup the interval when the component is unmounted or `start` becomes false
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // If `start` is false, clear the interval to stop the animation
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [sequences, gap, loop, start, endCallback]); // Watch for changes in `start`, `sequences`, `gap`, and `loop`

  return null; // No UI component is rendered, as this component handles the favicon animation
};

export default AnimateFavIcon;